import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'text' | 'accent' | 'danger';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  href?: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  rounded?: boolean;
  animation?: 'none' | 'pulse' | 'bounce' | 'scale';
}
const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  href,
  onClick,
  type = 'button',
  disabled = false,
  fullWidth = false,
  icon,
  iconPosition = 'left',
  loading = false,
  rounded = false,
  animation = 'none'
}) => {
  // Base styles
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  // Size styles
  const sizeStyles = {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2',
    lg: 'px-6 py-3 text-lg'
  };
  // Variant styles
  const variantStyles = {
    primary: 'bg-yellow-500 text-blue-900 hover:bg-yellow-400 focus:ring-yellow-500 shadow-sm hover:shadow',
    secondary: 'bg-blue-700 text-white hover:bg-blue-600 focus:ring-blue-700 shadow-sm hover:shadow',
    outline: 'border border-blue-700 text-blue-700 hover:bg-blue-50 focus:ring-blue-700',
    text: 'text-blue-700 hover:text-blue-900 hover:bg-blue-50 focus:ring-blue-700',
    accent: 'bg-green-600 text-white hover:bg-green-500 focus:ring-green-600 shadow-sm hover:shadow',
    danger: 'bg-red-600 text-white hover:bg-red-500 focus:ring-red-600 shadow-sm hover:shadow'
  };
  // Animation styles
  const animationStyles = {
    none: '',
    pulse: 'hover:animate-pulse',
    bounce: 'hover:animate-bounce',
    scale: 'hover:scale-105'
  };
  // Combined styles
  const combinedStyles = `
    ${baseStyles} 
    ${sizeStyles[size]} 
    ${variantStyles[variant]} 
    ${fullWidth ? 'w-full' : ''} 
    ${rounded ? 'rounded-full' : 'rounded-md'} 
    ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} 
    ${animationStyles[animation]}
    ${className}
  `;
  // Loading spinner
  const loadingSpinner = <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-582">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-583"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-584"></path>
    </svg>;
  // Content with icon
  const content = <>
      {loading && loadingSpinner}
      {!loading && icon && iconPosition === 'left' && <span className="mr-2" data-id="element-585">{icon}</span>}
      <span data-id="element-586">{children}</span>
      {!loading && icon && iconPosition === 'right' && <span className="ml-2" data-id="element-587">{icon}</span>}
    </>;
  // Motion props
  const motionProps = {
    whileHover: {
      scale: animation === 'scale' ? 1.05 : 1
    },
    whileTap: {
      scale: 0.98
    },
    transition: {
      duration: 0.2
    }
  };
  // Render as link if href is provided
  if (href) {
    return <motion.div {...motionProps} className="inline-block" data-id="element-588">
        <Link to={href} className={combinedStyles} onClick={disabled ? e => e.preventDefault() : undefined} data-id="element-589">
          {content}
        </Link>
      </motion.div>;
  }
  // Otherwise render as button
  return <motion.button type={type} className={combinedStyles} onClick={onClick} disabled={disabled || loading} {...motionProps} data-id="element-590">
      {content}
    </motion.button>;
};
export default Button;